/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import React, { useState, useCallback } from "react";
import { Button, TextField, FormControlLabel, Checkbox, Grid, Dialog, DialogContent } from "@material-ui/core";
import { isValidEmail } from "../../../../utility";

const settingValidation = (setting) => {
	if (setting && !isValidEmail(setting.deliveryEmail)) {
		return true;
	}
	return false;
};

/**
 * Component used to manage agents
 * @param {Boolean} showGlobalSettingsModal used to control open/closed state of the modal
 * @param {Set} setShowGlobalSettingsModal used to set the state of modal
 * @param {Object} globalSettings contains the global configurations.
 * @param {Function} saveGlobalSettingsHandler function use to save global settings.
 */
export const GlobalSettings = ({
	showGlobalSettingsModal,
	setShowGlobalSettingsModal,
	globalSettings,
	saveGlobalSettingsHandler,
}) => {
	const [setting, setSetting] = useState({});

	const updateProperty = useCallback((prop, value) => {
		setSetting((currentSetting) => ({ ...currentSetting, [prop]: value }));
	}, []);

	const resetSetting = () => {
		setSetting(globalSettings);
	};

	React.useEffect(() => {
		if (Object.keys(globalSettings).length === 0) {
			setSetting({ transcribeVoicemail: false, encryptVoicemail: false, deliveryEmail: "" });
		} else setSetting(globalSettings);
	}, [globalSettings]);

	const emailError = (email) => {
		if (email && !isValidEmail(email)) {
			return "Email address is invalid.";
		}
		return "";
	};
	const isValidSetting = setting && settingValidation(setting);
	return (
		<Dialog
			open={showGlobalSettingsModal}
			aria-labelledby="confirm-dialog-title"
			aria-describedby="confirm-dialog-description"
			onClose={() => setShowGlobalSettingsModal(false)}
		>
			<DialogContent>
				<h6 style={{ fontSize: 25, padding: 0, margin: 0 }}>Global Configurations</h6>
				<div style={{ marginTop: 15, marginBottom: 15 }}>
					<FormControlLabel
						control={
							<Checkbox
								name="transcribeEnabled"
								id="transcribe"
								onChange={(e) => {
									updateProperty("transcribeVoicemail", e.target.checked);
								}}
								checked={setting?.transcribeVoicemail || false}
								color="secondary"
							/>
						}
						label="Allow Voicemail Transcriptions"
					/>
					<FormControlLabel
						control={
							<Checkbox
								name="encryptionEnabled"
								id="encrypt"
								onChange={(e) => {
									updateProperty("encryptVoicemail", e.target.checked);
								}}
								checked={setting?.encryptVoicemail || false}
								color="secondary"
							/>
						}
						label="Enforce Voicemail Encryption"
					/>
					<TextField
						name="deliveryEmail"
						style={{ marginBottom: "20px" }}
						fullWidth
						onChange={(e) => {
							updateProperty("deliveryEmail", e.target.value);
						}}
						value={setting?.deliveryEmail || ""}
						label={"Delivery Email"}
						error={!!emailError(setting?.deliveryEmail)}
						helperText={emailError(setting?.deliveryEmail)}
					/>
					<p style={{ fontSize: "13px", color: "#808080" }}>
						Important: Delivery Email must be verified via Amazon Simple Email Service before emails can be delivered
					</p>
				</div>
				<Grid container spacing={2} direction="row" justifyContent="flex-end">
					<Grid item>
						<Button
							variant="outlined"
							color="primary"
							onClick={() => {
								setShowGlobalSettingsModal(false);
								resetSetting();
							}}
						>
							Cancel
						</Button>
					</Grid>
					<Grid item>
						<Button
							variant="contained"
							disabled={isValidSetting}
							style={{
								opacity: !isValidSetting ? 1 : 0.4,
								backgroundColor: "#cc1f20",
								color: "#fff",
								borderColor: "#cc1f20",
							}}
							onClick={() => {
								saveGlobalSettingsHandler(setting);
								setShowGlobalSettingsModal(false);
							}}
						>
							Save
						</Button>
					</Grid>
				</Grid>
			</DialogContent>
		</Dialog>
	);
};
